import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

const navLinks = [
  { key: 'nav.home', href: '#hero' },
  { key: 'nav.about', href: '#nosotros' },
  { key: 'nav.presentations', href: '#presentaciones' },
  { key: 'nav.catalog', href: '#catalogo' },
  { key: 'nav.benefits', href: '#beneficios' },
  { key: 'nav.locations', href: '#puntos-venta' },
  { key: 'nav.contact', href: '#contacto' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const currentLang = i18n.language.startsWith('en') ? 'en' : 'es';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const switchLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full pl-10 pr-6 md:pr-10 lg:pr-16">
        <div className="flex items-center justify-between h-[120px] lg:h-[160px]">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); handleNavClick('#hero'); }}
            className="flex items-center"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/66183ef1-dd3c-4346-a3f7-c9548432ed4a.png"
              alt="Kremsi Yogurt Griego"
              className="h-[110px] lg:h-[150px] w-auto object-contain"
            />
          </a>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1 ml-10">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors duration-300 rounded-md"
              >
                {t(link.key)}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3 md:gap-4">
            {/* Language selector */}
            <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5 border border-background-200">
              <button
                onClick={() => switchLanguage('es')}
                className={`whitespace-nowrap px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-300 ${
                  currentLang === 'es'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                ES
              </button>
              <button
                onClick={() => switchLanguage('en')}
                className={`whitespace-nowrap px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-300 ${
                  currentLang === 'en'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                EN
              </button>
            </div>

            {/* Social icons - desktop */}
            <a
              href="https://www.instagram.com/kremsi_cr?igsh=OXJ0M3pkNTF4YnJz"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex w-9 h-9 items-center justify-center text-foreground-500 hover:text-primary-500 transition-colors duration-300"
              aria-label="Instagram"
            >
              <i className="ri-instagram-line text-lg"></i>
            </a>
            <a
              href="https://www.tiktok.com/@kremsi_cr?_r=1&_t=ZS-98O4KMXJYij"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex w-9 h-9 items-center justify-center text-foreground-500 hover:text-primary-500 transition-colors duration-300"
              aria-label="TikTok"
            >
              <i className="ri-tiktok-line text-lg"></i>
            </a>

            {/* WhatsApp button */}
            <a
              href="https://wa.me/50670555703"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap hidden md:inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-300"
            >
              <i className="ri-whatsapp-line text-base"></i>
              <span className="hidden xl:inline">WhatsApp</span>
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-700"
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-400 ${
          mobileOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-background-50/98 backdrop-blur-md border-t border-background-200 px-6 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
              className="whitespace-nowrap px-3 py-3 text-sm font-medium text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors duration-200"
            >
              {t(link.key)}
            </a>
          ))}
          <div className="flex items-center gap-3 pt-3 border-t border-background-200 mt-2">
            <a
              href="https://www.instagram.com/kremsi_cr?igsh=OXJ0M3pkNTF4YnJz"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 flex items-center justify-center text-foreground-500 hover:text-primary-500 transition-colors"
            >
              <i className="ri-instagram-line text-lg"></i>
            </a>
            <a
              href="https://www.tiktok.com/@kremsi_cr?_r=1&_t=ZS-98O4KMXJYij"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 flex items-center justify-center text-foreground-500 hover:text-primary-500 transition-colors"
            >
              <i className="ri-tiktok-line text-lg"></i>
            </a>
            <a
              href="https://wa.me/50670555703"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all"
            >
              <i className="ri-whatsapp-line"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}